import type { CTRL_SCHEMA } from "@repo/sdk/types";
import {
  useHttpMutation,
  type HttpMutationConfiguration,
} from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

type FeedbackRequest = CTRL_SCHEMA["OpenapigenFeedbackRequest"];

/**
 * Hook for submitting feedback using SWR mutation
 *
 * @param config - Optional SWR mutation configuration
 * @returns SWR mutation response with trigger function, loading states, error, and data
 *
 * @example
 * ```tsx
 * function FeedbackForm() {
 *   const { trigger, isMutating, error } = useSubmitFeedback({
 *     onSuccess: () => {
 *       console.log('Feedback submitted successfully')
 *     },
 *     onError: (err) => {
 *       console.error('Failed to submit feedback:', err)
 *     }
 *   })
 *
 *   const handleSubmit = async (message: string) => {
 *     try {
 *       await trigger({ message, rating: 5 })
 *       console.log('Feedback submitted')
 *     } catch (err) {
 *       console.error('Error:', err)
 *     }
 *   }
 *
 *   return (
 *     <button onClick={() => handleSubmit('Great app!')} disabled={isMutating}>
 *       {isMutating ? 'Submitting...' : 'Submit Feedback'}
 *     </button>
 *   )
 * }
 * ```
 */
export function useSubmitFeedback(
  config?: HttpMutationConfiguration<string, Error, string, FeedbackRequest>
) {
  const client = useCtrlClient();

  return useHttpMutation<string, Error, string, FeedbackRequest>(
    "feedback/submit",
    async (_key, { arg }) => {
      return await client.submitFeedback(arg);
    },
    config
  );
}
